import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import CariForm from "@/components/CariForm";
import PaymentDialog from "@/components/PaymentDialog";
import CariImportDialog from "@/components/CariImportDialog";
import KasaImportDialog from "@/components/KasaImportDialog";
import SozlesmeDialog from "@/components/SozlesmeDialog";
import BulkSozlesmeDialog from "@/components/BulkSozlesmeDialog";
import DatePicker from "@/components/DatePicker";
import { api } from "@/lib/api";
import { fmt, fmtDate, DURUM_COLORS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Plus, Pencil, Trash2, Wallet, Search, X, Gavel, Upload, FileSpreadsheet, Sparkles, Flag } from "lucide-react";
import ColumnFilter from "@/components/ColumnFilter";

export default function CariRecords() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showKasa, setShowKasa] = useState(false);
  const [showSozlesme, setShowSozlesme] = useState(false);
  const [showBulkSoz, setShowBulkSoz] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [musteri, setMusteri] = useState("");
  const [sadeceIcralik, setSadeceIcralik] = useState(false);
  const [users, setUsers] = useState([]);
  const [yetkiliFilter, setYetkiliFilter] = useState("");
  const [colSel, setColSel] = useState({});
  const [sort, setSort] = useState({ key: null, dir: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (musteri) params.musteri = musteri;
      const { data } = await api.get("/cari/customers", { params });
      setRecords(data);
    } catch (e) {
      toast.error("Kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [musteri]);

  useEffect(() => {
    if (user?.role === "admin") api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, [user]);

  const renameCustomer = async (r) => {
    const yeni = window.prompt("Yeni müşteri adı:", r.musteri);
    if (!yeni || !yeni.trim() || yeni.trim() === r.musteri) return;
    try { await api.post("/cari/musteri-duzenle", { eski: r.musteri, yeni: yeni.trim() }); toast.success("Müşteri güncellendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
  };

  const deleteCustomer = (r) => {
    setConfirm({
      title: "Müşteriyi Sil",
      description: `${r.musteri} müşterisine ait TÜM vadeler (${r.vade_sayisi} kayıt) silinecek. Bu işlem geri alınamaz.`,
      onConfirm: async () => {
        try { const { data } = await api.post("/cari/musteri-sil", { musteri: r.musteri }); toast.success(`${data.deleted} vade silindi`); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
        finally { setConfirm(null); }
      },
    });
  };

  const moveCustomerToIcra = (r) => {
    setConfirm({
      title: "İcra Dosyasına Taşı",
      description: `${r.musteri} müşterisine ait ${r.vade_sayisi} vade TEK bir İcra dosyasına taşınacak. Kalan ${fmt(r.toplam_kalan)}, TC No ve ödeme geçmişi aktarılacak; cari kayıtları silinecek. Bu işlem geri alınamaz.`,
      onConfirm: async () => {
        try {
          const { data } = await api.post("/cari/musteri-to-icra", { musteri: r.musteri });
          toast.success(`İcra dosyası oluşturuldu — ${data.vade_sayisi} vade taşındı`);
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Taşıma başarısız");
        } finally {
          setConfirm(null);
        }
      },
    });
  };

  const importDetay = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/cari/import-detay", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.eklenen_vade} vade / ${data.musteri_sayisi} müşteri eklendi`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "İçe aktarılamadı");
    } finally {
      e.target.value = "";
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        await api.put(`/cari/${editing.id}`, payload);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post("/cari", payload);
        toast.success("Cari müşteri eklendi");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Kayıt kaydedilemedi");
    }
  };

  const handleDelete = (id) => {
    setConfirm({
      title: "Cari Kaydı Sil",
      description: "Bu cari kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
      onConfirm: async () => {
        try {
          await api.delete(`/cari/${id}`);
          toast.success("Kayıt silindi");
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Kayıt silinemedi");
        } finally {
          setConfirm(null);
        }
      },
    });
  };

  const toggleIcralik = async (r) => {
    try {
      await api.post("/cari/musteri-icralik", { musteri: r.musteri, icralik: !r.icralik });
      toast.success(!r.icralik ? `${r.musteri} İcralık olarak işaretlendi` : "İcralık işareti kaldırıldı");
      load();
    } catch (e) {
      toast.error("İşlem başarısız");
    }
  };

  const moveToIcra = async (r) => {
    if (!window.confirm(`${r.musteri} kaydı İcra paneline taşınsın mı? Kalan ${fmt(r.kalan)} icra tutarı olarak aktarılacak.`)) return;
    try {
      await api.post(`/cari/${r.id}/to-icra`);
      toast.success("Kayıt İcra paneline taşındı");
      load();
    } catch (e) {
      toast.error("Taşıma başarısız");
    }
  };

  const clearFilters = () => {
    setStart("");
    setEnd("");
    setMusteri("");
    setYetkiliFilter("");
    setColSel({});
    setSort({ key: null, dir: null });
  };

  const toggleSel = (key) => setSelected((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const toggleAll = () => setSelected((prev) => (prev.size === records.length ? new Set() : new Set(records.map((r) => r.key))));
  const bulkDelete = () => {
    const names = records.filter((r) => selected.has(r.key)).map((r) => r.musteri);
    setConfirm({
      title: "Seçili Müşterileri Sil",
      description: `${names.length} müşteri ve bunlara ait TÜM vadeler silinecek. Bu işlem geri alınamaz.`,
      onConfirm: async () => {
        try {
          const { data } = await api.post("/cari/musteri-sil-toplu", { musteriler: names });
          toast.success(`${data.deleted_customers} müşteri (${data.deleted_vade} vade) silindi`);
          setSelected(new Set());
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
          setConfirm(null);
        }
      },
    });
  };


  const exportExcel = async () => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (musteri) params.musteri = musteri;
      const res = await api.get("/cari/export/excel", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cari_musteriler.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch (e) {
      toast.error("Dışa aktarma başarısız");
    }
  };

  const nameOf = (email) => users.find((u) => u.email === email)?.name || email || "-";
  const yetkiliList = [...new Set(records.map((r) => r.yetkili).filter(Boolean))].sort();
  const COLS = [
    { key: "musteri", label: "Müşteri", type: "text" },
    { key: "tc_no", label: "TC No", type: "text" },
    { key: "vade_sayisi", label: "Vade Sayısı", type: "num" },
    { key: "en_yakin_vade", label: "En Yakın Vade", type: "date" },
    { key: "toplam_borc", label: "Toplam Borç", type: "money" },
    { key: "toplam_odenen", label: "Ödenen", type: "money" },
    { key: "toplam_kalan", label: "Kalan", type: "money" },
    { key: "durum", label: "Durum", type: "text" },
    { key: "yetkili", label: "Yetkili", type: "text" },
  ];
  const trLower = (s) => String(s ?? "").toLocaleLowerCase("tr");
  const disp = (c, r) => {
    const v = r[c.key];
    if (c.key === "yetkili") return r.yetkili ? nameOf(r.yetkili) : "-";
    if (c.type === "money") return fmt(v);
    if (c.type === "date") return v ? fmtDate(v) : "-";
    return String(v ?? "");
  };
  const distinct = (c) => {
    const arr = [...new Set(records.map((r) => disp(c, r)))];
    if (c.type === "money" || c.type === "num") arr.sort((a, b) => (parseFloat(String(a).replace(/[^\d,-]/g, "").replace(",", ".")) || 0) - (parseFloat(String(b).replace(/[^\d,-]/g, "").replace(",", ".")) || 0));
    else arr.sort((a, b) => trLower(a).localeCompare(trLower(b), "tr"));
    return arr;
  };
  const shown = records.filter((r) => (!sadeceIcralik || r.icralik) && (!yetkiliFilter || r.yetkili === yetkiliFilter) && COLS.every((c) => { const sel = colSel[c.key]; return !sel || sel.includes(disp(c, r)); }));
  let view = shown;
  if (sort.key) {
    const col = COLS.find((c) => c.key === sort.key);
    view = [...shown].sort((a, b) => {
      let av, bv;
      if (col.type === "money" || col.type === "num") { av = Number(a[sort.key]) || 0; bv = Number(b[sort.key]) || 0; }
      else if (col.type === "date") { av = a[sort.key] || ""; bv = b[sort.key] || ""; }
      else { av = trLower(disp(col, a)); bv = trLower(disp(col, b)); }
      return av < bv ? (sort.dir === "asc" ? -1 : 1) : av > bv ? (sort.dir === "asc" ? 1 : -1) : 0;
    });
  }
  const totals = {};
  COLS.forEach((c) => { if (c.type === "money") totals[c.key] = view.reduce((s, r) => s + (Number(r[c.key]) || 0), 0); });

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">CARİ TAKİP</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Cari Müşteriler</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSozlesme(true)}
            data-testid="cari-sozlesme-button"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#7C5E9B] bg-[#7C5E9B]/5 text-[#7C5E9B] rounded-sm text-sm font-medium hover:bg-[#7C5E9B]/10 transition-colors duration-150"
          >
            <Sparkles className="w-4 h-4" /> Sözleşmeden Oluştur
          </button>
          <button
            onClick={() => setShowBulkSoz(true)}
            data-testid="cari-bulk-sozlesme-button"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#7C5E9B] bg-white text-[#7C5E9B] rounded-sm text-sm font-medium hover:bg-[#7C5E9B]/5 transition-colors duration-150"
          >
            <Sparkles className="w-4 h-4" /> Toplu Sözleşme
          </button>
          <button
            onClick={() => setShowImport(true)}
            data-testid="cari-import-button"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
          >
            <Upload className="w-4 h-4 text-[#B8933F]" /> Excel ile Ekle
          </button>
          {(isAdmin || user?.role === "user") && (
            <button
              onClick={() => setShowKasa(true)}
              data-testid="cari-kasa-button"
              className="flex items-center gap-1.5 px-3 py-2 border border-[#B8933F] bg-white text-[#B8933F] rounded-sm text-sm font-medium hover:bg-[#B8933F]/5 transition-colors duration-150"
            >
              <Wallet className="w-4 h-4" /> Kasa Yükle
            </button>
          )}
          {(isAdmin || user?.role === "user") && (
            <>
              <input id="senet-toplu-input" type="file" accept="image/*,.pdf" multiple className="hidden" data-testid="senet-toplu-input"
                onChange={async (e) => {
                  const fl = Array.from(e.target.files || []); e.target.value = "";
                  if (!fl.length) return;
                  if (!window.confirm(`${fl.length} dosya için senet kırpılıp SENEDİ OLMAYAN carilere eklenecek (dosya adı = müşteri adı veya TC). Devam?`)) return;
                  const fd = new FormData(); fl.forEach((f) => fd.append("files", f));
                  const tid = toast.loading("Senetler kırpılıyor… (biraz sürebilir)");
                  try {
                    const { data } = await api.post("/cari/senet-toplu", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success(`${data.eklenen}/${data.toplam} dosya eklendi`, { id: tid });
                    const fails = (data.sonuclar || []).filter((r) => r.durum !== "eklendi");
                    if (fails.length) toast.message(`${fails.length} eklenmedi: ` + fails.slice(0, 6).map((r) => `${r.dosya} (${r.durum})`).join(", "));
                    load();
                  } catch (err) { toast.error("Toplu senet işlemi başarısız", { id: tid }); }
                }} />
              <label htmlFor="senet-toplu-input" data-testid="senet-toplu-button"
                className="flex items-center gap-1.5 px-3 py-2 border border-[#B8933F] bg-white text-[#B8933F] rounded-sm text-sm font-medium hover:bg-[#B8933F]/5 cursor-pointer transition-colors duration-150">
                <Upload className="w-4 h-4" /> Toplu Senet Ekle
              </label>
            </>
          )}
          <button
            onClick={exportExcel}
            data-testid="cari-export-button"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
          >
            <FileSpreadsheet className="w-4 h-4 text-[#2F6E4E]" /> Excel
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            data-testid="add-cari-button"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"
          >
            <Plus className="w-4 h-4" /> Yeni Cari Müşteri
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-5 p-4 bg-white border border-[#E7DECC] rounded-sm">
        <div className="w-[170px]">
          <label className="kicker block mb-1.5">VADE BAŞLANGIÇ</label>
          <DatePicker value={start} onChange={setStart} placeholder="Başlangıç" testId="cari-filter-start" />
        </div>
        <div className="w-[170px]">
          <label className="kicker block mb-1.5">VADE BİTİŞ</label>
          <DatePicker value={end} onChange={setEnd} placeholder="Bitiş" testId="cari-filter-end" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="kicker block mb-1.5">MÜŞTERİ ARA</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8377]" />
            <input
              value={musteri}
              onChange={(e) => setMusteri(e.target.value)}
              data-testid="cari-filter-musteri"
              placeholder="Müşteri adı..."
              className="w-full pl-9 pr-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]"
            />
          </div>
        </div>
        {(start || end || musteri || yetkiliFilter) && (
          <button onClick={clearFilters} data-testid="cari-clear-filters" className="flex items-center gap-1 px-3 py-2 text-sm text-[#8C8377] hover:text-[#A63A3A] transition-colors duration-150">
            <X className="w-4 h-4" /> Temizle
          </button>
        )}
        <div className="w-[190px]">
          <label className="kicker block mb-1.5">YETKİLİ</label>
          <select value={yetkiliFilter} onChange={(e) => setYetkiliFilter(e.target.value)} data-testid="cari-filter-yetkili" className="w-full px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm focus:outline-none focus:border-[#B8933F]">
            <option value="">Yetkili: Tümü</option>
            {yetkiliList.map((y) => <option key={y} value={y}>{nameOf(y)}</option>)}
          </select>
        </div>
        <button
          onClick={() => setSadeceIcralik((v) => !v)}
          data-testid="cari-filter-icralik"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium border transition-colors duration-150 ${sadeceIcralik ? "border-[#B8933F] bg-[#B8933F]/10 text-[#B8933F]" : "border-[#E7DECC] bg-white text-[#8C8377] hover:bg-[#EFE7D6]"}`}
        >
          <Flag className="w-4 h-4" /> Sadece İcralık
        </button>
      </div>

      {isAdmin && selected.size > 0 && (
        <div className="flex items-center justify-between mb-3 px-4 py-2 bg-[#A63A3A]/5 border border-[#A63A3A]/30 rounded-sm" data-testid="cari-bulk-bar">
          <span className="text-sm font-medium text-[#26231E]">{selected.size} müşteri seçildi</span>
          <button onClick={bulkDelete} data-testid="cari-bulk-delete" className="flex items-center gap-1.5 px-3 py-1.5 bg-[#A63A3A] hover:bg-[#D32F2F] text-white rounded-sm text-sm font-medium transition-colors duration-150">
            <Trash2 className="w-4 h-4" /> Seçilenleri Sil
          </button>
        </div>
      )}

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto max-h-[calc(100vh-16rem)]" data-testid="cari-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              {isAdmin && <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={records.length > 0 && selected.size === records.length} onChange={toggleAll} data-testid="cari-select-all" /></th>}
              {COLS.map((c) => (
                <th key={c.key} className={`px-3 py-2.5 kicker whitespace-nowrap ${c.type === "money" || c.type === "num" ? "text-right" : "text-left"}`}>
                  <div className={`flex items-center gap-1 ${c.type === "money" || c.type === "num" ? "justify-end" : "justify-between"}`}>
                    <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                    <ColumnFilter col={c} values={distinct(c)} selected={colSel[c.key] ?? null} onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))} sortDir={sort.key === c.key ? sort.dir : null} onSort={(dir) => setSort({ key: c.key, dir })} onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                  </div>
                </th>
              ))}
              <th className="px-3 py-2.5 kicker text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 11 : 10} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
            ) : view.length === 0 ? (
              <tr><td colSpan={isAdmin ? 11 : 10} className="px-3 py-10 text-center text-[#8C8377]" data-testid="cari-empty">Kayıt yok. "Yeni Cari Müşteri" ile başlayın.</td></tr>
            ) : (
              view.map((r, i) => {
                const dc = DURUM_COLORS[r.durum] || (r.durum === "Devam Ediyor" ? "#B8933F" : "#8C8377");
                return (
                  <tr key={r.key} onClick={() => navigate(`/cari-musteri/${encodeURIComponent(r.musteri)}`)} className={`border-b border-[#E7DECC] hover:bg-[#EFE7D6] cursor-pointer transition-colors duration-150 ${i % 2 === 1 ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`cari-cust-row-${i}`}>
                    {isAdmin && <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(r.key)} onChange={() => toggleSel(r.key)} data-testid={`cari-select-${i}`} /></td>}
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-[#B8933F]">{r.musteri}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[#8C8377]">{r.tc_no || "-"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.vade_sayisi}</td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">{r.en_yakin_vade ? fmtDate(r.en_yakin_vade) : "-"}</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{fmt(r.toplam_borc)}</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-[#2F6E4E]">{fmt(r.toplam_odenen)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${r.toplam_kalan > 0 ? "text-[#A63A3A]" : "text-[#2F6E4E]"}`}>{fmt(r.toplam_kalan)}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ backgroundColor: `${dc}1a`, color: dc }} data-testid={`cari-cust-durum-${i}`}>{r.durum}</span>{r.icralik && <span className="ml-1 inline-block px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/15 text-[#B8933F]" data-testid={`cari-cust-icralik-badge-${i}`}>İcralık</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[#8C8377]" data-testid={`cari-cust-yetkili-${i}`}>{r.yetkili ? nameOf(r.yetkili) : "-"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end items-center gap-1">
                        <button onClick={() => toggleIcralik(r)} title={r.icralik ? "İcralık işaretini kaldır" : "İcralık olarak işaretle"} data-testid={`cari-cust-icralik-${i}`} className={`p-1.5 rounded-sm transition-colors duration-150 ${r.icralik ? "text-[#B8933F] bg-[#B8933F]/10" : "text-[#8C8377] hover:text-[#B8933F] hover:bg-white"}`}><Flag className="w-4 h-4" /></button>
                        <button onClick={() => navigate(`/cari-musteri/${encodeURIComponent(r.musteri)}`)} data-testid={`cari-cust-detay-${i}`} className="px-3 py-1 text-[#B8933F] border border-[#E7DECC] rounded-sm text-xs font-medium hover:bg-white transition-colors duration-150">Föy / Detay</button>
                        {isAdmin && (
                          <>
                            <button onClick={() => renameCustomer(r)} title="Adı düzenle" data-testid={`cari-cust-edit-${i}`} className="p-1.5 text-[#8C8377] hover:text-[#B8933F] hover:bg-white rounded-sm transition-colors duration-150"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => deleteCustomer(r)} title="Müşteriyi sil" data-testid={`cari-cust-delete-${i}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] hover:bg-white rounded-sm transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {view.length > 0 && (
            <tfoot className="sticky bottom-0 bg-[#EFE7D6] border-t-2 border-[#B8933F] z-10">
              <tr>
                {isAdmin && <td className="px-3 py-2"></td>}
                {COLS.map((c) => (
                  <td key={c.key} className={`px-3 py-2 whitespace-nowrap text-xs font-bold ${c.type === "money" ? "text-right tabular-nums" : "text-left"}`}>
                    {c.key === "musteri" ? `${view.length} müşteri` : c.type === "money" ? fmt(totals[c.key]) : ""}
                  </td>
                ))}
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showForm && (
        <CariForm initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSubmit={handleSubmit} />
      )}
      {payFor && (
        <PaymentDialog
          record={payFor}
          onClose={() => { setPayFor(null); load(); }}
          onChanged={(updated) => {
            setPayFor(updated);
            setRecords((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          }}
        />
      )}
      {showImport && (
        <CariImportDialog onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
      )}
      {showKasa && (
        <KasaImportDialog onClose={() => setShowKasa(false)} onDone={() => { setShowKasa(false); load(); }} />
      )}
      {showSozlesme && (
        <SozlesmeDialog open={showSozlesme} onClose={() => setShowSozlesme(false)} onDone={() => load()} />
      )}
      {showBulkSoz && (
        <BulkSozlesmeDialog open={showBulkSoz} onClose={() => setShowBulkSoz(false)} onDone={() => load()} />
      )}
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        onConfirm={confirm?.onConfirm}
      />
    </Layout>
  );
}
